import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useCallback } from 'react';
import { Card, Row, Col, Spinner, Alert, Button, Form, InputGroup, Modal, ListGroup, Dropdown, Table } from 'react-bootstrap';
import { getConfig, updateConfig } from '../../services/config/settings';
import { useAnalytics, downloadClickDetailsCSV } from '../../services/api/hooks/useAnalytics';
import { useSubscribers } from '../../services/api/hooks/useSubscribers';
import { getBackendUrl } from '../../services/config/backendUrl';
import { getAuthHeaders } from '../../services/auth/authHeaders';

interface AnalyticsProps {
  campaignId: number;
}



interface FailedRecord {
  reason: string;
  email: string;
  status: string;
}

interface FailedReasonsData {
  campaignName: string;
  failedRecords: FailedRecord[];
}

const Analytics = forwardRef<{ refresh: () => void }, AnalyticsProps & { onLoadingChange?: (loading: boolean) => void }>(({ campaignId, onLoadingChange }, ref) => {
  const [failedReasons, setFailedReasons] = useState<FailedReasonsData | null>(null);
  const [processedData, setProcessedData] = useState<{
    smtpGroups: Array<{
      code: string;
      reasons: string[];
      emails: string[];
      count: number;
    }>;
    domainGroups: Array<{
      domain: string;
      emails: string[];
      reasons: string[];
      count: number;
    }>;
    totalFailed: number;
    statusBreakdown: {
      bounce: number;
      bounced: number;
      failed: number;
      expiration: number;
      rejection: number;
      obb: number;
    };
  } | null>(null);
  const [loadingFailedReasons, setLoadingFailedReasons] = useState(false);
  const [reasonsLimit, setReasonsLimit] = useState(100);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [modalEmails, setModalEmails] = useState<string[]>([]);
  const [modalTitle, setModalTitle] = useState('');
  const [showVariantsModal, setShowVariantsModal] = useState(false);
  const [modalVariants, setModalVariants] = useState<string[]>([]);
  const [variantsModalTitle, setVariantsModalTitle] = useState('');
  const [domainFilter, setDomainFilter] = useState('');
  const [smtpCodeFilter, setSmtpCodeFilter] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<string | null>(null);
  const [showSuggestionsModal, setShowSuggestionsModal] = useState(false);
  const [autoOptoutCodes, setAutoOptoutCodes] = useState<string[]>([]);
  const [showClickDetails, setShowClickDetails] = useState(false);
  const [clickSummary, setClickSummary] = useState<any[]>([]);
  const [loadingClickDetails, setLoadingClickDetails] = useState(false);
  const clickDetailsRef = useRef<HTMLDivElement>(null);

  const [addingCodes, setAddingCodes] = useState<Set<string>>(new Set());
  const [removingCodes, setRemovingCodes] = useState<Set<string>>(new Set());
  const chartRef = useRef<HTMLCanvasElement>(null);

  const { analytics, loading, error, fetchAnalytics, fetchFailedReasons, analyzeFailures } = useAnalytics(campaignId);
  const { bulkOptOut } = useSubscribers();

  const drawChart = useCallback(() => {
    const canvas = chartRef.current;
    if (!canvas || !analytics) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    canvas.width = 300;
    canvas.height = 250;
    
    const delivered = analytics.consolidatedStats?.delivered || 0;
    const opened = analytics.consolidatedStats?.read || 0;
    const clicked = analytics.consolidatedStats?.click || 0;
    const bounced = (analytics.statusBreakdown?.bounce || 0) + (analytics.statusBreakdown?.bounced || 0) + (analytics.statusBreakdown?.failed || 0) + (analytics.statusBreakdown?.rejection || 0) + (analytics.statusBreakdown?.obb || 0);
    const expired = analytics.statusBreakdown?.expiration || 0;
    
    const data = [
      { label: 'Delivered', value: delivered, color: '#28a745' },
      { label: 'Opened', value: opened, color: '#ffc107' },
      { label: 'Clicked', value: clicked, color: '#343a40' },
      { label: 'Bounced', value: bounced, color: '#dc3545' },
      { label: 'Expired', value: expired, color: '#6c757d' }
    ].filter(item => item.value > 0);
    
    const total = data.reduce((sum, item) => sum + item.value, 0);
    if (total === 0) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#6c757d';
      ctx.font = '16px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('No data available', canvas.width / 2, canvas.height / 2);
      return;
    }
    
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2 - 20;
    const radius = 80;
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    let currentAngle = -Math.PI / 2;
    
    data.forEach((item) => {
      const sliceAngle = (item.value / total) * 2 * Math.PI;
      
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.arc(centerX, centerY, radius, currentAngle, currentAngle + sliceAngle);
      ctx.closePath();
      ctx.fillStyle = item.color;
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.stroke();
      
      currentAngle += sliceAngle;
    });
    
    let legendY = centerY + radius + 20;
    data.forEach((item, index) => {
      const x = 20;
      const y = legendY + (index * 16);
      ctx.fillStyle = item.color;
      ctx.fillRect(x, y, 10, 10);
      ctx.fillStyle = '#333';
      ctx.font = '11px Arial';
      ctx.textAlign = 'left';
      const percentage = ((item.value / analytics.totalEmails) * 100).toFixed(1);
      ctx.fillText(`${item.label}: ${percentage}%`, x + 14, y + 8);
    });
  }, [analytics]);

  const getSmtpCodeMeaning = (code: string): string => {
    const meanings: { [key: string]: string } = {
      '421': 'Service not available',
      '450': 'Mailbox temporarily unavailable',
      '451': 'Local error in processing',
      '452': 'Insufficient system storage',
      '500': 'Syntax error',
      '501': 'Syntax error in parameters',
      '502': 'Command not implemented',
      '503': 'Bad sequence of commands',
      '504': 'Command parameter not implemented',
      '511': 'Bad email address',
      '550': 'Mailbox unavailable',
      '551': 'User not local',
      '552': 'Exceeded storage allocation',
      '553': 'Mailbox name not allowed',
      '554': 'Transaction failed'
    };
    return meanings[code] || 'Unknown error';
  };

  const processFailedReasonsData = (data: FailedReasonsData, domainFilter: string, smtpCodeFilter: string) => {
    // Calculate status breakdown from failed records
    const statusBreakdown = {
      bounce: 0,
      bounced: 0, 
      failed: 0,
      expiration: 0,
      rejection: 0,
      obb: 0
    };
    
    data.failedRecords.forEach(record => {
      const status = record.status.toLowerCase();
      if (statusBreakdown.hasOwnProperty(status)) {
        statusBreakdown[status as keyof typeof statusBreakdown]++;
      }
    });
    let filteredRecords = data.failedRecords.map(record => {
      if (!record.reason) {
        return { ...record, reason: JSON.stringify({ code: '500', message: 'Unknown error - no reason provided' }) };
      }
      return record;
    });
    
    if (domainFilter.trim() || smtpCodeFilter.trim()) {
      filteredRecords = filteredRecords.filter(record => {
        let domainMatch = true;
        let smtpMatch = true;
        
        if (domainFilter.trim()) {
          const domain = record.email.split('@')[1] || '';
          domainMatch = domain.toLowerCase().includes(domainFilter.toLowerCase());
        }
        
        if (smtpCodeFilter.trim()) {
          let code = '500';
          try {
            const parsed = JSON.parse(record.reason);
            if (parsed.code) {
              code = parsed.code.toString();
            }
          } catch {
            const codeMatch = record.reason.match(/^(\d{3})/);
            code = codeMatch ? codeMatch[1] : '500';
          }
          smtpMatch = code.includes(smtpCodeFilter);
        }
        
        return domainMatch && smtpMatch;
      });
    }
    
    const codeGroups = new Map();
    filteredRecords.forEach(record => {
      let code = '500';
      try {
        const parsed = JSON.parse(record.reason);
        if (parsed.code) {
          code = parsed.code.toString();
        }
      } catch {
        const codeMatch = record.reason.match(/^(\d{3})/);
        code = codeMatch ? codeMatch[1] : '500';
      }
      
      if (!codeGroups.has(code)) {
        codeGroups.set(code, {
          code,
          reasons: new Set(),
          emails: [],
          count: 0
        });
      }
      const group = codeGroups.get(code);
      group.reasons.add(record.reason);
      group.emails.push(record.email);
      group.count++;
    });
    
    const domainGroups = new Map();
    filteredRecords.forEach(record => {
      const domain = record.email.split('@')[1] || 'unknown';
      if (!domainGroups.has(domain)) {
        domainGroups.set(domain, {
          domain,
          emails: [],
          reasons: new Set(),
          count: 0
        });
      }
      const group = domainGroups.get(domain);
      group.emails.push(record.email);
      if (record.reason) {
        group.reasons.add(record.reason);
      }
      group.count++;
    });
    
    return {
      smtpGroups: Array.from(codeGroups.values())
        .map(group => ({
          ...group,
          reasons: Array.from(group.reasons) as string[]
        }))
        .sort((a, b) => b.count - a.count),
      domainGroups: Array.from(domainGroups.values())
        .map(group => ({
          ...group,
          reasons: Array.from(group.reasons) as string[]
        }))
        .sort((a, b) => b.count - a.count),
      totalFailed: filteredRecords.length,
      statusBreakdown
    };
  };

  const handleFetchFailedReasons = async () => {
    try {
      setLoadingFailedReasons(true);
      const data = await fetchFailedReasons(reasonsLimit, selectedStatuses) as FailedReasonsData;
      setFailedReasons(data);
      setProcessedData(processFailedReasonsData(data, domainFilter, smtpCodeFilter));
    } catch (err) {
      console.error('Failed to fetch failed reasons:', err);
    } finally {
      setLoadingFailedReasons(false);
    }
  };

  const handleAnalyzeFailures = async () => {
    try {
      setIsAnalyzing(true);
      const data = await analyzeFailures() as any;
      setAiSuggestions(data.suggestions);
      setShowSuggestionsModal(true);
    } catch (err) {
      console.error('Failed to analyze failures:', err);
      alert('Failed to analyze failures. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleOptOut = async (emails: string[], code: string) => {
    try {
      await bulkOptOut(emails);
    } catch (err) {
      console.error('Failed to opt out subscribers:', err);
      throw err;
    }
  };

  const handleAddToConfig = async (code: string) => {
    try {
      setAddingCodes(prev => new Set(prev).add(code));
      
      const currentCodes = autoOptoutCodes.filter(c => c.trim());
      const newCodes = [...currentCodes, code].join(',');
      
      const success = await updateConfig('auto_optout_res_codes', newCodes);
      
      if (success) {
        setAutoOptoutCodes([...currentCodes, code]);
        
        // Find emails for this code and opt them out immediately
        const codeGroup = processedData?.smtpGroups.find(group => group.code === code);
        if (codeGroup && codeGroup.emails.length > 0) {
          await handleOptOut(codeGroup.emails, code);
        }
        
        alert(`Response code ${code} added to auto opt-out configuration and ${codeGroup?.emails.length || 0} subscribers opted out`);
      } else {
        throw new Error('Failed to update configuration');
      }
    } catch (err) {
      console.error('Failed to add code to config:', err);
      alert('Failed to add code to configuration. Please try again.');
    } finally {
      setAddingCodes(prev => {
        const newSet = new Set(prev);
        newSet.delete(code);
        return newSet;
      });
    }
  };

  const handleRemoveFromConfig = async (code: string) => {
    try {
      setRemovingCodes(prev => new Set(prev).add(code));
      
      const currentCodes = autoOptoutCodes.filter(c => c.trim() && c !== code);
      const newCodes = currentCodes.join(',');
      
      const success = await updateConfig('auto_optout_res_codes', newCodes);
      
      if (success) {
        setAutoOptoutCodes(currentCodes);
        alert(`Response code ${code} removed from auto opt-out configuration`);
      } else {
        throw new Error('Failed to update configuration');
      }
    } catch (err) {
      console.error('Failed to remove code from config:', err);
      alert('Failed to remove code from configuration. Please try again.');
    } finally {
      setRemovingCodes(prev => {
        const newSet = new Set(prev);
        newSet.delete(code);
        return newSet;
      });
    }
  };

  useEffect(() => {
    if (failedReasons) {
      setProcessedData(processFailedReasonsData(failedReasons, domainFilter, smtpCodeFilter));
    }
  }, [domainFilter, smtpCodeFilter, failedReasons]);

  const [showIpBlacklistWarning, setShowIpBlacklistWarning] = useState(false);
  const [code522Details, setCode522Details] = useState<any[]>([]);
  const [showCode522Details, setShowCode522Details] = useState(false);

  const handleFetchAnalytics = useCallback(async (refresh = false) => {
    try {
      if (refresh) {
        setShowClickDetails(false);
      }
      onLoadingChange?.(true);
      const data = await fetchAnalytics(refresh) as any;
      if (data) {
        setReasonsLimit(data.consolidatedStats?.failed || 100);
        // Set all failed statuses as selected by default
        const allFailedStatuses = ['bounce', 'bounced', 'failed', 'expiration', 'rejection', 'obb'];
        setSelectedStatuses(allFailedStatuses);
        
        // Check for code 522 records from backend
        if (data.code522Details && data.code522Details.length > 0) {
          setShowIpBlacklistWarning(true);
          setCode522Details(data.code522Details);
        } else {
          setShowIpBlacklistWarning(false);
          setCode522Details([]);
          setShowCode522Details(false);
        }
      }
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    } finally {
      onLoadingChange?.(false);
    }
  }, [fetchAnalytics, onLoadingChange]);

  useEffect(() => {

    const fetchAutoOptoutConfig = async () => {
      try {
        const codes = await getConfig('auto_optout_res_codes');
        if (codes) {
          setAutoOptoutCodes(codes.split(',').map(code => code.trim()));
        }
      } catch (err) {
        console.error('Failed to fetch auto opt-out config:', err);
      }
    };

    handleFetchAnalytics();
    fetchAutoOptoutConfig();
  }, [campaignId, handleFetchAnalytics]);

  useImperativeHandle(ref, () => ({
    refresh: () => handleFetchAnalytics(true)
  }));

  useEffect(() => {
    if (analytics) {
      drawChart();
    }
  }, [analytics, drawChart]);

  // Fetch click details when section is shown
  useEffect(() => {
    if (showClickDetails && campaignId) {
      const fetchClickData = async () => {
        setLoadingClickDetails(true);
        try {
          const backendUrl = getBackendUrl();
          const summaryResponse = await fetch(`${backendUrl}/api/campaign/${campaignId}/clicks`, {
            headers: getAuthHeaders(),
          });
          const summaryData = await summaryResponse.json();
          setClickSummary(Array.isArray(summaryData) ? summaryData : []);
        } catch (err) {
          console.error('Error fetching click summary:', err);
        } finally {
          setLoadingClickDetails(false);
        }
      };
      fetchClickData();
    }
  }, [showClickDetails, campaignId]);

  if (loading) {
    return (
      <div className="m-3">
        <Card>
          <Card.Body className="text-center">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading analytics...</span>
            </Spinner>
            <p className="mt-2">Loading campaign analytics...</p>
          </Card.Body>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="m-3">
        <Alert variant="danger">
          <Alert.Heading>Error Loading Analytics</Alert.Heading>
          <p>{error}</p>
        </Alert>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="m-3">
        <Alert variant="warning">
          <p>No analytics data available for this campaign.</p>
        </Alert>
      </div>
    );
  }



  return (
    <div className="m-2" style={{ maxWidth: '100vw', overflowX: 'hidden' }}>
      <Card>
        <Card.Header as="h5" style={{ backgroundColor: '#e1f5fe' }}>
          <div className="d-flex justify-content-between align-items-center">
            <span>Campaign Analytics: {analytics.campaignName}</span>
          </div>
        </Card.Header>
        <Card.Body>
          {showIpBlacklistWarning && (
            <Alert variant="warning" className="mb-4">
              <Alert.Heading>⚠️ IP Blacklist Detected ({code522Details.length} affected emails)</Alert.Heading>
              <p className="mb-2">
                <strong>Your server IP is on industry blacklists.</strong>
                {code522Details.length > 0 && (() => {
                  try {
                    const firstRecord = JSON.parse(code522Details[0].reason || '{}');
                    const ipMatch = firstRecord.content?.match(/from ([0-9.]+)/);
                    const blacklistMatch = firstRecord.content?.match(/\(([^)]+)\)/);
                    return (
                      <span>
                        {ipMatch && <> IP: <strong>{ipMatch[1]}</strong></>}
                        {blacklistMatch && <> on {blacklistMatch[1]}</>}
                      </span>
                    );
                  } catch {
                    return null;
                  }
                })()}
              </p>
              <p className="mb-2">
                This is blocking email delivery. To resolve:
              </p>
              <ul className="mb-2">
                <li>Contact the blacklist providers directly to request removal</li>
                <li>Check your server IP reputation and sending practices</li>
                <li>Consider using a different IP or email service provider</li>
              </ul>
              <div className="mb-2">
                <Button 
                  variant="outline-warning" 
                  size="sm" 
                  onClick={() => setShowCode522Details(!showCode522Details)}
                >
                  {showCode522Details ? 'Hide Details' : 'View Details'}
                </Button>
              </div>
              {showCode522Details && (
                <div className="mt-3">
                  <h6>Affected Emails:</h6>
                  <div className="table-responsive" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    <table className="table table-sm table-striped">
                      <thead>
                        <tr>
                          <th>Email</th>
                          <th>Status</th>
                          <th>IP Address</th>
                          <th>Blacklist Info</th>
                          <th>Command</th>
                        </tr>
                      </thead>
                      <tbody>
                        {code522Details.map((record, index) => {
                          let parsedReason = null;
                          try {
                            parsedReason = JSON.parse(record.reason || '{}');
                          } catch {
                            parsedReason = { content: record.reason || 'Unknown error' };
                          }
                          
                          const ipMatch = parsedReason.content?.match(/from ([0-9.]+)/);
                          const dateMatch = parsedReason.content?.match(/on ([0-9/]+ [0-9:]+ [A-Z]+)/);
                          const blacklistMatch = parsedReason.content?.match(/\(([^)]+)\)/);
                          
                          return (
                            <tr key={index}>
                              <td><small>{record.email}</small></td>
                              <td><span className="badge bg-danger">{record.status}</span></td>
                              <td><small className="text-info">{ipMatch ? ipMatch[1] : 'N/A'}</small></td>
                              <td>
                                <small className="text-muted">
                                  {blacklistMatch ? blacklistMatch[1] : 'Industry blacklists'}
                                  {dateMatch && <><br/><span className="text-warning">{dateMatch[1]}</span></>}
                                </small>
                              </td>
                              <td><small className="text-secondary">{parsedReason.command?.replace('\r\n', '') || 'N/A'}</small></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              <p className="mb-0">
                <small className="text-muted">Our postmaster cannot resolve blacklist issues - you must contact the blacklist providers directly.</small>
              </p>
            </Alert>
          )}
          <Row className="mb-4">
            <Col md={6}>
              <Card className="border-primary h-100">
                <Card.Header>
                  <h5 className="mb-0">📊 Campaign Summary</h5>
                </Card.Header>
                <Card.Body className="p-0 pb-0">
                  <div className="alert alert-info py-2 mx-3 mt-3 mb-2" style={{ fontSize: '0.8rem' }}>
                    <strong>Email Journey:</strong> A → B (queued) → C (received by server) → D (delivered to inbox) → E (opened by user) → F (clicked by user). G shows bounced/failed emails.
                  </div>
                  <div className="table-responsive">
                    <table className="table table-striped table-hover mb-0">
                      <tbody>
                        <tr>
                          <td>A. Campaign Subscribers</td>
                          <td className="text-end">{analytics.totalEmails.toLocaleString()}</td>
                        </tr>
                        <tr>
                          <td>B. Processing Queue</td>
                          <td className="text-end">{Math.max(0, (analytics.totalEmails ?? 0) - (analytics.consolidatedStats?.received ?? 0)).toLocaleString()}</td>
                        </tr>
                        <tr>
                          <td>C. MTA: Received</td>
                          <td className="text-end">{(analytics.consolidatedStats?.received || 0).toLocaleString()}</td>
                        </tr>
                        <tr>
                          <td>D. MTA: Delivered ({analytics.totalEmails > 0 ? (((analytics.consolidatedStats?.delivered || 0) / analytics.totalEmails) * 100).toFixed(1) : '0.0'}%)</td>
                          <td className="text-end">{(analytics.consolidatedStats?.delivered || 0).toLocaleString()}</td>
                        </tr>
                        <tr>
                          <td>E. User: Opened ({analytics.totalEmails > 0 ? (((analytics.consolidatedStats?.read || 0) / analytics.totalEmails) * 100).toFixed(1) : '0.0'}%)</td>
                          <td className="text-end">{(analytics.consolidatedStats?.read || 0).toLocaleString()}</td>
                        </tr>
                        <tr>
                          <td>
                          F. User: Clicks ({analytics.totalEmails > 0 ? (((analytics.consolidatedStats?.click || 0) / analytics.totalEmails) * 100).toFixed(1) : '0.0'}%)
                          {(analytics.consolidatedStats?.click || 0) > 0 && !showClickDetails && (
                            <Button
                              variant="link"
                              size="sm"
                              className="ms-2 p-0"
                              onClick={() => {
                                setShowClickDetails(true);
                                setTimeout(() => {
                                  clickDetailsRef.current?.scrollIntoView({ behavior: 'smooth' });
                                }, 100);
                              }}
                              title="View click details"
                              style={{ fontSize: '0.9rem' }}
                            >
                              ➕
                            </Button>
                          )}
                        </td>
                          <td className="text-end">{(analytics.consolidatedStats?.click || 0).toLocaleString()}</td>
                        </tr>
                        <tr>
                          <td>G. MTA: Bounce ({analytics.totalEmails > 0 ? (((analytics.consolidatedStats?.failed || 0) / analytics.totalEmails) * 100).toFixed(1) : '0.0'}%)</td>
                          <td className="text-end">{(analytics.consolidatedStats?.failed || 0).toLocaleString()}</td>
                        </tr>
                        {(analytics.consolidatedStats?.unsubscribed || 0) > 0 && (
                          <tr>
                            <td>H. User: Unsubscribed ({analytics.totalEmails > 0 ? (((analytics.consolidatedStats?.unsubscribed || 0) / analytics.totalEmails) * 100).toFixed(1) : '0.0'}%)</td>
                            <td className="text-end">{(analytics.consolidatedStats?.unsubscribed || 0).toLocaleString()}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="border-info h-100">
                <Card.Header>
                  <h5 className="mb-0">📈 Performance Chart</h5>
                </Card.Header>
                <Card.Body className="d-flex align-items-center justify-content-center">
                  <div className="text-center">
                    <canvas ref={chartRef} width="300" height="300" style={{ maxWidth: '100%', height: 'auto' }}></canvas>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          
          {showClickDetails && (
            <Row className="mb-4">
              <Col md={12}>
                <div ref={clickDetailsRef}>
                  <Card className="border-primary">
                    <Card.Header className="d-flex justify-content-between align-items-center">
                      <h5 className="mb-0">Click Details</h5>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => {
                          const fetchAndDownload = async () => {
                            setLoadingClickDetails(true);
                            try {
                              const backendUrl = getBackendUrl();
                              const response = await fetch(`${backendUrl}/api/campaign/${campaignId}/clicks/detail`, {
                                headers: getAuthHeaders(),
                              });
                              const data = await response.json();
                              if (Array.isArray(data)) {
                                downloadClickDetailsCSV(data, `campaign-${campaignId}`);
                              }
                            } catch (err) {
                              console.error('Error downloading CSV:', err);
                            } finally {
                              setLoadingClickDetails(false);
                            }
                          };
                          fetchAndDownload();
                        }}
                        disabled={loadingClickDetails}
                      >
                        {loadingClickDetails ? 'Loading...' : 'Download CSV'}
                      </Button>
                    </Card.Header>
                    <Card.Body>
                      {loadingClickDetails ? (
                        <div className="text-center py-4">
                          <Spinner animation="border" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </Spinner>
                        </div>
                      ) : (
                        <>
                          <div className="alert alert-info py-2" style={{ fontSize: '0.85rem' }}>
                            Unsubscribe link clicks are grouped into one row (path?data=...) and all counts may differ from actual user clicks because the same user can click multiple times.
                          </div>
                          <h6 className="mb-3">URL Breakdown</h6>
                          <Table striped bordered hover size="sm" className="mb-4">
                            <thead className="table-light">
                              <tr>
                                <th style={{ width: '70%' }}>URL</th>
                                <th className="text-center" style={{ width: '30%' }}>Clicks</th>
                              </tr>
                            </thead>
                            <tbody>
                              {clickSummary.length === 0 ? (
                                <tr>
                                  <td colSpan={2} className="text-center text-muted">No clicks recorded</td>
                                </tr>
                              ) : (
                                clickSummary.map((item, idx) => (
                                  <tr key={idx}>
                                    <td className="text-truncate" style={{ maxWidth: 0 }}>
                                      <a href={item.url} target="_blank" rel="noopener noreferrer" title={item.url}>
                                        {item.url}
                                      </a>
                                    </td>
                                    <td className="text-center fw-bold">{item.clicks.toLocaleString()}</td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </Table>
                        </>
                      )}
                    </Card.Body>
                  </Card>
                </div>
              </Col>
            </Row>
          )}

          {analytics.consolidatedStats.failed > 0 && (
            <Row className="mb-4">
              <Col md={12}>
                <Card>
                  <Card.Header>
                    <div className="d-flex justify-content-between align-items-center">
                      <span>Failed Report Section</span>
                      <div className="d-flex align-items-center gap-2">
                        <InputGroup size="sm" style={{ width: '120px', display: 'none' }}>
                          <Form.Control
                            type="number"
                            min="1"
                            value={reasonsLimit}
                            onChange={(e) => setReasonsLimit(Math.max(parseInt(e.target.value) || 1, 1))}
                          />
                        </InputGroup>
                        <Dropdown>
                          <Dropdown.Toggle variant="outline-secondary" size="sm" disabled={!analytics}>
                            Status ({selectedStatuses.length})
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            {[
                              { key: 'bounce', label: 'Bounce' },
                              { key: 'bounced', label: 'Bounced' },
                              { key: 'failed', label: 'Failed' },
                              { key: 'expiration', label: 'Expiration' },
                              { key: 'rejection', label: 'Rejection' },
                              { key: 'obb', label: 'OOB' }
                            ].map(status => (
                              <Dropdown.Item
                                key={status.key}
                                as="label"
                                style={{ cursor: 'pointer' }}
                              >
                                <Form.Check
                                  type="checkbox"
                                  id={`status-${status.key}`}
                                  label={`${status.label}${processedData?.statusBreakdown?.[status.key as keyof typeof processedData.statusBreakdown] ? ` (${processedData.statusBreakdown[status.key as keyof typeof processedData.statusBreakdown].toLocaleString()})` : ''}`}
                                  checked={selectedStatuses.includes(status.key)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedStatuses([...selectedStatuses, status.key]);
                                    } else {
                                      setSelectedStatuses(selectedStatuses.filter(s => s !== status.key));
                                    }
                                  }}
                                />
                              </Dropdown.Item>
                            ))}
                          </Dropdown.Menu>
                        </Dropdown>
                        <Button 
                          variant="outline-danger" 
                          size="sm" 
                          onClick={handleFetchFailedReasons}
                          disabled={loadingFailedReasons}
                          className="me-2"
                        >
                          {loadingFailedReasons ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-1" />
                              Loading...
                            </>
                          ) : (
                            'Fetch Failed Reasons'
                          )}
                        </Button>
                        {analytics && analytics.consolidatedStats.failed > 0 && (
                          <Button 
                            variant="outline-primary" 
                            size="sm" 
                            onClick={handleAnalyzeFailures}
                            disabled={isAnalyzing}
                            className="me-2"
                          >
                            {isAnalyzing ? (
                              <>
                                <Spinner animation="border" size="sm" className="me-1" />
                                Analyzing...
                              </>
                            ) : (
                              'AI Analyzer'
                            )}
                          </Button>
                        )}
                        <InputGroup size="sm" style={{ width: '150px' }}>
                          <Form.Control
                            type="text"
                            placeholder="Filter domains..."
                            value={domainFilter}
                            onChange={(e) => setDomainFilter(e.target.value)}
                            disabled={!failedReasons}
                          />
                        </InputGroup>
                        <InputGroup size="sm" style={{ width: '120px' }}>
                          <Form.Control
                            type="text"
                            placeholder="SMTP code..."
                            value={smtpCodeFilter}
                            onChange={(e) => setSmtpCodeFilter(e.target.value)}
                            disabled={!failedReasons}
                          />
                        </InputGroup>
                      </div>
                    </div>
                  </Card.Header>
                <Card.Body>
                  <div className="mb-3">
                    <h6 className="mb-3">Failed Count Breakdown:</h6>
                    <Row className="g-3">
                      <Col md={2}>
                        <Card className="text-center border-primary h-100">
                          <Card.Body className="py-2">
                            <h5 className="text-primary mb-1">{analytics.consolidatedStats.failed.toLocaleString()}</h5>
                            <small className="text-muted">Total Failed</small>
                          </Card.Body>
                        </Card>
                      </Col>
                      {processedData && (
                        <>
                          {processedData.statusBreakdown?.bounce > 0 && (
                            <Col md={2}>
                              <Card className="text-center border-danger h-100">
                                <Card.Body className="py-2">
                                  <h5 className="text-danger mb-1">{processedData.statusBreakdown.bounce.toLocaleString()}</h5>
                                  <small className="text-muted">Bounce</small>
                                </Card.Body>
                              </Card>
                            </Col>
                          )}
                          {processedData.statusBreakdown?.bounced > 0 && (
                            <Col md={2}>
                              <Card className="text-center border-warning h-100">
                                <Card.Body className="py-2">
                                  <h5 className="text-warning mb-1">{processedData.statusBreakdown.bounced.toLocaleString()}</h5>
                                  <small className="text-muted">Bounced</small>
                                </Card.Body>
                              </Card>
                            </Col>
                          )}
                          {processedData.statusBreakdown?.failed > 0 && (
                            <Col md={2}>
                              <Card className="text-center border-secondary h-100">
                                <Card.Body className="py-2">
                                  <h5 className="text-secondary mb-1">{processedData.statusBreakdown.failed.toLocaleString()}</h5>
                                  <small className="text-muted">Failed</small>
                                </Card.Body>
                              </Card>
                            </Col>
                          )}
                          {processedData.statusBreakdown?.expiration > 0 && (
                            <Col md={2}>
                              <Card className="text-center border-dark h-100">
                                <Card.Body className="py-2">
                                  <h5 className="text-dark mb-1">{processedData.statusBreakdown.expiration.toLocaleString()}</h5>
                                  <small className="text-muted">Expiration</small>
                                </Card.Body>
                              </Card>
                            </Col>
                          )}
                        </>
                      )}
                    </Row>
                  </div>

                  <hr />

                  {!failedReasons && !loadingFailedReasons && (
                    <p className="text-muted">Click "Fetch Failed Reasons" to load SMTP failure analysis.</p>
                  )}
                  {processedData && processedData.smtpGroups.length > 0 && (
                    <div>
                      <h6>Failed Records Analysis ({processedData.totalFailed} total):</h6>
                      <div className="alert alert-info py-2 mb-3" style={{ fontSize: '0.85rem' }}>
                        <strong>Auto Opt-Out Controls:</strong> <strong>Add</strong> button enables automatic opt-out for future emails with this response code and immediately opts out current subscribers. <strong>Remove</strong> button disables automatic opt-out for this response code.
                      </div>
                      <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto', overflowX: 'auto' }}>
                        <table className="table table-sm table-striped">
                          <thead className="table-dark sticky-top">
                            <tr>
                              <th style={{ minWidth: '300px', maxWidth: '350px' }}>Reason</th>
                              <th style={{ minWidth: '60px' }}>Code</th>
                              <th style={{ minWidth: '120px' }}>Meaning</th>
                              <th style={{ minWidth: '120px' }}>Emails</th>
                              <th style={{ minWidth: '60px' }}>Count</th>
                              <th style={{ minWidth: '50px' }}>%</th>
                              <th style={{ minWidth: '80px' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {processedData.smtpGroups.map((group, index) => {
                              const meaning = getSmtpCodeMeaning(group.code);
                              const percentage = ((group.count / processedData.totalFailed) * 100).toFixed(1);
                              const displayEmails = group.emails.slice(0, 1);
                              const hasMore = group.emails.length > 1;
                              const displayReason = group.reasons[0] && group.reasons[0].length > 50 ? `${group.reasons[0].substring(0, 50)}...` : group.reasons[0] || '';
                              
                              return (
                                <tr key={index}>
                                  <td style={{ maxWidth: '350px', wordBreak: 'break-word' }}>
                                    <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                      {displayReason}
                                      {(group.reasons.length > 1 || (group.reasons[0] && group.reasons[0].length > 50)) && (
                                        <span 
                                          className="text-primary" 
                                          style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                          onClick={() => {
                                            setModalVariants(group.reasons);
                                            setVariantsModalTitle(`All reason variants for SMTP ${group.code} (${group.reasons.length} total)`);
                                            setShowVariantsModal(true);
                                          }}
                                        >
                                          {group.reasons.length > 1 ? ` (+${group.reasons.length - 1} variants)` : ' (view full)'}
                                        </span>
                                      )}
                                    </small>
                                  </td>
                                  <td><span className="badge bg-danger">{group.code}</span></td>
                                  <td><small>{meaning}</small></td>
                                  <td>
                                    <small>
                                      {displayEmails.join(', ')}
                                      {hasMore && (
                                        <span 
                                          className="text-primary" 
                                          style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                          onClick={() => {
                                            setModalEmails(group.emails);
                                            setModalTitle(`All emails for SMTP ${group.code} (${group.emails.length} total)`);
                                            setShowEmailModal(true);
                                          }}
                                        >
                                          {' '}...+{group.emails.length - 1} more
                                        </span>
                                      )}
                                    </small>
                                  </td>
                                  <td><strong>{group.count}</strong></td>
                                  <td><span className="text-danger">{percentage}%</span></td>
                                  <td>
                                    {group.code && group.code !== '0' && group.code.match(/^\d{3}$/) ? (
                                      autoOptoutCodes.includes(group.code) ? (
                                        <div className="d-flex gap-1">
                                          <Button
                                            variant="outline-primary"
                                            size="sm"
                                            disabled={removingCodes.has(group.code)}
                                            onClick={() => {
                                              if (window.confirm(`Remove response code ${group.code} from auto opt-out configuration?`)) {
                                                handleRemoveFromConfig(group.code);
                                              }
                                            }}
                                            title={`Remove response code ${group.code} from auto opt-out config`}
                                          >
                                            {removingCodes.has(group.code) ? (
                                              <>
                                                <Spinner animation="border" size="sm" className="me-1" />
                                                Removing...
                                              </>
                                            ) : (
                                              'Remove'
                                            )}
                                          </Button>
                                        </div>
                                      ) : (
                                        <Button
                                          variant="outline-danger"
                                          size="sm"
                                          disabled={addingCodes.has(group.code)}
                                          onClick={() => {
                                            if (window.confirm(`Add response code ${group.code} to auto opt-out configuration and immediately opt out ${group.emails.length} subscriber(s)?`)) {
                                              handleAddToConfig(group.code);
                                            }
                                          }}
                                          title={`Add response code ${group.code} to auto opt-out config`}
                                        >
                                          {addingCodes.has(group.code) ? (
                                            <>
                                              <Spinner animation="border" size="sm" className="me-1" />
                                              Adding...
                                            </>
                                          ) : (
                                            'Add'
                                          )}
                                        </Button>
                                      )
                                    ) : null}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  {failedReasons && failedReasons.failedRecords && failedReasons.failedRecords.length === 0 && (
                    <p className="text-success">No failed records found for this campaign.</p>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
          )}
          
          {processedData && processedData.domainGroups.length > 0 && (
            <Row>
              <Col md={12}>
                <Card>
                  <Card.Header>
                    <span>Domain-wise Failures</span>
                  </Card.Header>
                  <Card.Body>
                    {processedData && processedData.domainGroups.length > 0 ? (
                      <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto', overflowX: 'auto' }}>
                        <table className="table table-sm table-striped">
                          <thead className="table-dark sticky-top">
                            <tr>
                              <th style={{ minWidth: '120px' }}>Domain</th>
                              <th style={{ minWidth: '60px' }}>Count</th>
                              <th style={{ minWidth: '50px' }}>%</th>
                              <th style={{ minWidth: '120px' }}>Emails</th>
                              <th style={{ minWidth: '250px' }}>Reasons</th>
                            </tr>
                          </thead>
                          <tbody>
                            {processedData.domainGroups.map((group, index) => {
                              const percentage = ((group.count / processedData.totalFailed) * 100).toFixed(1);
                              const displayEmails = group.emails.slice(0, 1);
                              const hasMoreEmails = group.emails.length > 1;
                              const displayReasons = group.reasons.slice(0, 2);
                              const hasMoreReasons = group.reasons.length > 2;
                              
                              return (
                                <tr key={index}>
                                  <td><strong>{group.domain}</strong></td>
                                  <td><strong>{group.count}</strong></td>
                                  <td><span className="text-danger">{percentage}%</span></td>
                                  <td>
                                    <small>
                                      {displayEmails.join(', ')}
                                      {hasMoreEmails && (
                                        <span 
                                          className="text-primary" 
                                          style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                          onClick={() => {
                                            setModalEmails(group.emails);
                                            setModalTitle(`All emails for domain ${group.domain} (${group.emails.length} total)`);
                                            setShowEmailModal(true);
                                          }}
                                        >
                                          {' '}...+{group.emails.length - 1} more
                                        </span>
                                      )}
                                    </small>
                                  </td>
                                  <td style={{ maxWidth: '250px', wordBreak: 'break-word' }}>
                                    <small className="text-muted">
                                      {displayReasons.map(reason => 
                                        reason.length > 25 ? `${reason.substring(0, 25)}...` : reason
                                      ).join('; ')}
                                      {hasMoreReasons && (
                                        <span 
                                          className="text-primary" 
                                          style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                          onClick={() => {
                                            setModalVariants(group.reasons);
                                            setVariantsModalTitle(`All reasons for domain ${group.domain} (${group.reasons.length} total)`);
                                            setShowVariantsModal(true);
                                          }}
                                        >
                                          {' '}...+{group.reasons.length - 2} more
                                        </span>
                                      )}
                                    </small>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-muted">No domain data available.</p>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>
      
      <Modal show={showEmailModal} onHide={() => setShowEmailModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{modalTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
          <ListGroup variant="flush">
            {modalEmails.map((email, index) => (
              <ListGroup.Item key={index} className="py-1">
                <small>{email}</small>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEmailModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      
      <Modal show={showVariantsModal} onHide={() => setShowVariantsModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{variantsModalTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
          <ListGroup variant="flush">
            {modalVariants.map((variant, index) => (
              <ListGroup.Item key={index} className="py-2">
                <small className="text-muted">{variant}</small>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowVariantsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      
      <Modal show={showSuggestionsModal} onHide={() => setShowSuggestionsModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>🤖 AI Analysis & Suggestions</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '500px', overflowY: 'auto' }}>
          {aiSuggestions ? (
            <div className="ai-suggestions">
              {aiSuggestions.split('\n').map((line, index) => {
                if (line.startsWith('## ')) {
                  return <h5 key={index} className="text-primary mt-3 mb-2">{line.replace('## ', '')}</h5>;
                } else if (line.startsWith('- ')) {
                  return <div key={index} className="mb-1"><span className="text-success">•</span> {line.replace('- ', '')}</div>;
                } else if (line.trim()) {
                  return <p key={index} className="mb-2">{line}</p>;
                }
                return null;
              })}
            </div>
          ) : (
            <p>No suggestions available.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSuggestionsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
});

export default Analytics;

Analytics.displayName = 'Analytics';
